"use client"

import { href, Link, useLocation } from "react-router-dom"
import {
  Calendar,
  LayoutDashboard,
 MessageCircleDashed,
 Wallet
 
} from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { useSelector } from 'react-redux';
import md5 from "blueimp-md5"
import { useState ,useEffect} from "react";

export default function MentorSideBar({ isOpen, toggleSidebar, isMobile }) {
  const location = useLocation()

  const startupId = localStorage.getItem("startupId")
  const logoUrl = useSelector((state) => state.logo.logoUrl);

  const { data: startupData } = useQuery({
    queryKey: ["startup-profile", startupId],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/startups/${startupId}`)
      return response.data
    },
    enabled: !!startupId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false, 
    keepPreviousData: true,
  })

  const fullName = startupData?.startupName || ""
  const email = startupData?.email || ""
  const username = startupData?.username || fullName || ""
  const displayInitials = (username || "").slice(0, 2).toUpperCase()

  const gravatarHash = email ? md5(email.trim().toLowerCase()) : null
  const gravatarUrl = gravatarHash
    ? `https://www.gravatar.com/avatar/${gravatarHash}?s=40&d=identicon`
    : null

  const [imageError, setImageError] = useState(false);

  const navItems = [
    { name: "Dashboard", icon: LayoutDashboard, href: "/mentordashboard" },
    { name: "Meetings", icon: Calendar, href: "/mentorcalendor" },
    { name: "PayoutRequests", icon: Wallet, href: "/mentorpayout" },
  
  ]

  const getSidebarWidth = () => {
    if (isMobile) return 0
    return 80
  }

  useEffect(() => {
  if (isMobile) {
    toggleSidebar(true);
  }
}, [isMobile]);


  return (
    <>
  {/* Mobile overlay */}
{isMobile && isOpen && (
  <div
    className="fixed inset-0 bg-black/70 backdrop-blur-md z-40 transition-all duration-300"
    onClick={toggleSidebar}
  />
)}

<div
  className={`fixed inset-y-0 left-0 z-50
  bg-gradient-to-b from-[#0f172a] via-[#020617] to-[#020617]
  text-slate-100 transition-all duration-300
  border-r border-white/5
 
  flex flex-col
  ${isMobile
  ? isOpen
    ? "translate-x-0"
    : "-translate-x-full"
  : "translate-x-0 w-20"}
`}
>

  {/* Close button (mobile) */}
  {isMobile && (
    <button
      onClick={toggleSidebar}
      className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition"
    >
      ✕
    </button>
  )}

  {/* Logo Section */}
  <div
    className={`flex items-center flex-shrink-0 p-4 h-20
    border-b border-white/5 bg-black/20 backdrop-blur-xl
    ${isMobile ? "justify-start" : "justify-center"}`}
  >
    <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ring-1 ring-white/10 bg-gradient-to-br from-blue-500/20 to-purple-500/20">
      <img src="/tac_logo.png" alt="Tactos Logo" className="w-full h-full object-cover rounded-xl" />
    </div>

    {isMobile && (
      <div className="ml-4">
        <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Tactos
        </h1>
        <p className="text-xs text-slate-400">Admin Panel</p>
      </div>
    )}
  </div>

  {/* Navigation */}
  <div className="flex-1 overflow-hidden">
    <nav className="h-full overflow-y-auto scrollbar-thin px-3 py-6">
      <ul className="space-y-3">
        {navItems.map((item, index) => {
          const IconComponent = item.icon
          const isActive = location.pathname === item.href

          return (
            <li key={item.name} className="relative group">
              <Link
                to={item.href}
                className={`flex items-center rounded-xl transition-all duration-300 relative
                ${isActive
                  ? "bg-gradient-to-r from-blue-600/25 via-indigo-500/20 to-purple-600/25 text-blue-300 shadow-xl shadow-blue-500/10 ring-1 ring-white/10 scale-[1.04]"
                  : "text-slate-300 hover:text-white hover:bg-white/[0.06] hover:shadow-lg hover:shadow-black/30 hover:scale-105"
                }
                ${isMobile ? "justify-start p-4" : "justify-center p-3"}`}
                onClick={isMobile ? toggleSidebar : undefined}
              >
                {/* Active Indicator */}
                {isActive && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400 to-purple-400 rounded-r-full" />
                )}

                {/* Icon */}
                <IconComponent className="w-5 h-5" />

                {/* Text */}
                {isMobile && (
                  <span className="ml-4 font-medium">{item.name}</span>
                )}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  </div>

  {/* Bottom User Section */}
  {isMobile && (
    <div className="p-4 border-t border-white/10 bg-gradient-to-t from-black/60 to-transparent backdrop-blur-xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-400 to-blue-500 shadow-[0_0_30px_rgba(59,130,246,0.35)] flex items-center justify-center">
          <span className="text-sm font-bold text-white">
            {displayInitials}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold">{fullName}</p>
          <p className="text-xs text-slate-400">{email}</p>
        </div>
      </div>
    </div>
  )}
</div>

    </>
  )
}
